import { components } from '@/generated/internal-api/openapi-types';
import { GroupApiId, StudyGroupV1 } from '@/model/TreatmentGroup.model';
import { AnimalApiId, AnimalDefinitionV1, AnimalV1 } from '@/model/Animal.model';
import { Nullable, TempApiID } from '@/model/Common.model';
import { AnimalDefinitionApiId } from '@/model/PlannedAnimal.model';
import { MetadataGlossaryApiId } from '@/model/Metadata.model';
import { TeamGlossaryApiId } from '@/model/Glosary.model';

export type SampleDetailType = components['schemas']['SampleDetailType.schema'];
export type SampleDefinitionApiId = components['schemas']['SampleDefinitionApiId.schema'];
export type SampleDefinitionDetailV1 = components['schemas']['SampleDefinitionDetailV1.schema'];
export type SampleDefinitionMetadataV1 = components['schemas']['SampleDefinitionMetadataV1.schema'];
export type SampleDetailUnit = components['schemas']['SampleDetailUnit.schema'];
export type SampleDefinitionV1 = components['schemas']['SampleDefinitionV1.schema'];
export type SampleDefinitionUpsertV1 = components['schemas']['SampleDefinitionUpsertV1.schema'];
export type SampleDefinitionCreateV1 = components['schemas']['SampleDefinitionCreateV1.schema'];
export type SamplePlanExportType = components['schemas']['SamplePlanExportType.schema'];
export type CollectionStatus = components['schemas']['SampleCollectionStatus.schema'];
export type PlannedSampleSheetCreateV1 = components['schemas']['PlannedSampleSheetCreateV1.schema'];
export type SamplingSheetBaseColumn = components['schemas']['SamplingSheetBaseColumn.schema'];
export type SamplingSheetColumnId = SamplingSheetBaseColumn | MetadataGlossaryApiId;

export const COLLECTION_STATUS_TOOLTIPS: Record<CollectionStatus, string | undefined> = {
  not_configured: 'Sample type group assignment not configured',
  unavailable: 'No registered animals available for sample collection',
  metadata_required: 'Required metadata is missing from sample definitions',
  available: undefined,
};

export const plannedSampleExportType: Record<SamplePlanExportType, SamplePlanExportType> = {
  sample_plan: 'sample_plan',
} as const;

/**
 * Planned Sample Form Data
 */

export type TempSampleTypeId = TempApiID<'tgl'>;
export type TempSampleDefinitionApiId = TempApiID<'sdf'>;
export type TempGroupApiId = components['schemas']['TempGroupApiId.schema'];
export type TempAnimalApiId = components['schemas']['TempAnimalApiId.schema'];
export type TempAnimalDefinitionApiId = components['schemas']['TempAnimalDefinitionApiId.schema'];
export type GroupTarget = Pick<StudyGroupV1, 'color' | 'name' | 'max_subjects' | 'no'> & {
  api_id: GroupApiId | TempGroupApiId;
};
export type RegisteredAnimalTarget = Pick<AnimalV1, 'name'> & {
  api_id: AnimalApiId | TempAnimalApiId;
  study_group_api_id: Nullable<GroupTarget['api_id']>;
};
export type PlannedAnimalTarget = Pick<AnimalDefinitionV1, 'planned_id' | 'animal_api_id'> & {
  api_id: AnimalDefinitionApiId | TempAnimalDefinitionApiId;
  study_group_api_id: GroupTarget['api_id'];
};
export type TargetApiId = GroupTarget['api_id'] | RegisteredAnimalTarget['api_id'] | PlannedAnimalTarget['api_id'];
type TargetApiIdMap = {
  groups: GroupTarget['api_id'];
  registered_animals: RegisteredAnimalTarget['api_id'];
  planned_animals: PlannedAnimalTarget['api_id'];
  no_target: never;
};
export type SampleDefinitionTargetFormData =
  | {
      [K in keyof Omit<TargetApiIdMap, 'no_target'>]: {
        type: K;
        targets: Array<TargetApiIdMap[K]>;
      };
    }[keyof Omit<TargetApiIdMap, 'no_target'>]
  | { type: 'no_target' };
export type SampleDefinitionFormData = Omit<SampleDefinitionV1, 'api_id' | 'target' | 'created_at' | 'updated_at'> & {
  api_id: SampleDefinitionApiId | TempSampleDefinitionApiId;
  target: SampleDefinitionTargetFormData;
};

export const CollectionMethod = {
  quick_collect: 'quick_collect',
  sampling_sheet: 'sampling_sheet',
} as const;

export type CollectionMethodType = (typeof CollectionMethod)[keyof typeof CollectionMethod];

/**
 * Sample Definition Validation
 */

export type SampleDefinitionIssue =
  | {
      type: 'archived-sample-type';
      sample_type_id: TeamGlossaryApiId;
      sample_type_name: string;
    }
  | {
      type: 'missing-sample-type';
      sample_type_id: TeamGlossaryApiId;
    }
  | {
      type: 'invalid-sample-detail-type';
      invalid_detail_types: Array<SampleDetailType>;
      sample_type_name: string;
    }
  | {
      type: 'inactive-metadata';
      inactive_metadata_ids: Array<MetadataGlossaryApiId>;
      metadata_names: Array<string>;
    }
  | {
      type: 'missing-metadata';
      missing_metadata_ids: Array<MetadataGlossaryApiId>;
    }
  | {
      type: 'invalid-metadata-value';
      metadata_id: MetadataGlossaryApiId;
      metadata_name: string;
      stored_value: string;
      available_options: Array<string>;
    }
  | {
      type: 'invalid-target';
      target:
        | {
            type: 'groups';
            missing_group_ids: Array<GroupTarget['api_id']>;
            group_names: Array<string>;
          }
        | {
            type: 'registered_animals';
            missing_animal_ids: Array<RegisteredAnimalTarget['api_id']>;
            animal_names: Array<string>;
          }
        | {
            type: 'planned_animals';
            missing_animal_ids: Array<PlannedAnimalTarget['api_id']>;
          };
    };

export type SampleDefinitionIssueAction =
  | { type: 'close-modal' }
  | {
      type: 'remove-definition';
      definition_id: SampleDefinitionFormData['api_id'];
    }
  | {
      type: 'remove-and-edit';
      definition_id: SampleDefinitionFormData['api_id'];
    }
  | {
      type: 'remove-invalid-metadata';
      definition_id: SampleDefinitionFormData['api_id'];
      metadata_ids: Array<MetadataGlossaryApiId>;
    }
  | {
      type: 'remove-invalid-details';
      definition_id: SampleDefinitionFormData['api_id'];
      detail_types: Array<SampleDetailType>;
    }
  | {
      type: 'change-sample-type';
      definition_id: SampleDefinitionFormData['api_id'];
    }
  | {
      type: 'remove-invalid-targets';
      definition_id: SampleDefinitionFormData['api_id'];
      target_type: 'groups';
      target_ids: Array<GroupTarget['api_id']>;
    }
  | {
      type: 'remove-invalid-targets';
      definition_id: SampleDefinitionFormData['api_id'];
      target_type: 'registered_animals';
      target_ids: Array<RegisteredAnimalTarget['api_id']>;
    }
  | {
      type: 'remove-invalid-targets';
      definition_id: SampleDefinitionFormData['api_id'];
      target_type: 'planned_animals';
      target_ids: Array<PlannedAnimalTarget['api_id']>;
    }
  | {
      type: 'bulk-remove-definitions';
      definition_ids: Array<SampleDefinitionFormData['api_id']>;
    }
  | {
      type: 'bulk-remove-invalid-metadata';
      definition_ids: Array<SampleDefinitionFormData['api_id']>;
      metadata_ids: Array<MetadataGlossaryApiId>;
    }
  | {
      type: 'bulk-remove-invalid-details';
      definition_ids: Array<SampleDefinitionFormData['api_id']>;
      detail_types: Array<SampleDetailType>;
    }
  | {
      type: 'bulk-remove-invalid-targets';
      definition_ids: Array<SampleDefinitionFormData['api_id']>;
      target_type: 'groups';
      target_ids: Array<GroupTarget['api_id']>;
    }
  | {
      type: 'bulk-remove-invalid-targets';
      definition_ids: Array<SampleDefinitionFormData['api_id']>;
      target_type: 'registered_animals';
      target_ids: Array<RegisteredAnimalTarget['api_id']>;
    }
  | {
      type: 'bulk-remove-invalid-targets';
      definition_ids: Array<SampleDefinitionFormData['api_id']>;
      target_type: 'planned_animals';
      target_ids: Array<PlannedAnimalTarget['api_id']>;
    };
